// gioco/video.ts — videochiamata diretta tra i due telefoni (WebRTC). Il video non passa mai dal server di gioco:
// il server inoltra solo i messaggi per mettersi d'accordo (offerta, risposta, candidati). Se i telefoni non si
// raggiungono direttamente, il video passa dal TURN di Cloudflare.
//
// "Perfect negotiation" (schema raccomandato dal W3C): entrambi possono proporre; se propongono insieme, quello
// "educato" (lo sfidato) cede. Se la connessione si rompe si riprova con un riavvio ICE.
import type { DatiSegnale } from "../../../condiviso/protocollo.ts";
import type { ServerIce } from "../servizi/sfide.ts";

export type StatoVideo = "collegamento" | "collegato" | "interrotto" | "fallito";

export interface OpzioniVideo {
  iceServers: ServerIce[];
  educato: boolean;
  locale: MediaStream;
  inviaSegnale: (d: DatiSegnale) => void;
  /** v038: video più leggero (partita a 4: ogni telefono manda 3 video): kbit/s massimi e altezza massima. */
  limiteVideo?: { kbps: number; altezza: number };
  /** Modalità di prova: solo candidati TURN (per provare il relè). */
  soloTurn?: boolean;
  suRemoto: (s: MediaStream) => void;
  suStato: (s: StatoVideo) => void;
}

/** Quanto aspettare in "interrotto" prima di provare un riavvio ICE. */
const ATTESA_RIAVVIO_MS = 3_000;

export class Videochiamata {
  private pc: RTCPeerConnection;
  private o: OpzioniVideo;
  private facendoOfferta = false;
  private ignoraOfferta = false;
  private chiusa = false;
  private timerRiavvio: ReturnType<typeof setTimeout> | null = null;
  private remoto = new MediaStream();

  constructor(o: OpzioniVideo) {
    this.o = o;
    this.pc = new RTCPeerConnection({ iceServers: o.iceServers.map((s) => ({ ...s })), iceTransportPolicy: o.soloTurn ? "relay" : "all" });
    for (const traccia of o.locale.getTracks()) this.pc.addTrack(traccia, o.locale);

    this.pc.ontrack = (ev) => {
      // Una sola MediaStream remota: le tracce arrivano una per volta.
      if (!this.remoto.getTracks().includes(ev.track)) this.remoto.addTrack(ev.track);
      this.o.suRemoto(this.remoto);
    };
    this.pc.onicecandidate = (ev) => {
      const c = ev.candidate;
      this.o.inviaSegnale({
        candidato: c
          ? { candidate: c.candidate, sdpMid: c.sdpMid, sdpMLineIndex: c.sdpMLineIndex, usernameFragment: c.usernameFragment }
          : null,
      });
    };
    this.pc.onnegotiationneeded = async () => {
      try {
        this.facendoOfferta = true;
        await this.pc.setLocalDescription();
        const d = this.pc.localDescription;
        if (d && (d.type === "offer" || d.type === "answer")) this.o.inviaSegnale({ descrizione: { type: d.type, sdp: d.sdp } });
      } catch {
        // si riproverà alla prossima negoziazione
      } finally {
        this.facendoOfferta = false;
      }
    };
    this.pc.onconnectionstatechange = () => this.aggiornaStato();
    this.pc.oniceconnectionstatechange = () => {
      if (this.pc.iceConnectionState === "failed") this.pc.restartIce();
    };
    this.o.suStato("collegamento");
  }

  private aggiornaStato(): void {
    if (this.chiusa) return;
    const s = this.pc.connectionState;
    if (this.timerRiavvio) clearTimeout(this.timerRiavvio);
    this.timerRiavvio = null;
    if (s === "connected") {
      this.o.suStato("collegato");
      void this.applicaLimite();
    }
    else if (s === "failed") {
      this.o.suStato("fallito");
      this.pc.restartIce();
    } else if (s === "disconnected") {
      this.o.suStato("interrotto");
      this.timerRiavvio = setTimeout(() => {
        if (!this.chiusa && this.pc.connectionState === "disconnected") this.pc.restartIce();
      }, ATTESA_RIAVVIO_MS);
    } else if (s === "connecting" || s === "new") this.o.suStato("collegamento");
  }

  /** v038: tetto alla qualità del video in uscita (dopo la negoziazione: prima i parametri non ci sono ancora). */
  private async applicaLimite(): Promise<void> {
    const l = this.o.limiteVideo;
    if (!l || l.kbps <= 0) return;
    for (const s of this.pc.getSenders()) {
      if (s.track?.kind !== "video") continue;
      try {
        const p = s.getParameters();
        if (!p.encodings?.length) p.encodings = [{}];
        const h = s.track.getSettings().height ?? 0;
        for (const e of p.encodings) {
          e.maxBitrate = l.kbps * 1000;
          if (l.altezza > 0 && h > l.altezza) e.scaleResolutionDownBy = h / l.altezza;
        }
        await s.setParameters(p);
      } catch {
        // il browser non lo permette: si resta alla qualità normale
      }
    }
  }

  /** I messaggi uno dopo l'altro: due descrizioni insieme vedrebbero uno stato della negoziazione vecchio. */
  private coda: Promise<void> = Promise.resolve();

  ricevi(d: DatiSegnale): Promise<void> {
    this.coda = this.coda.then(() => this.riceviOra(d));
    return this.coda;
  }

  private async riceviOra(d: DatiSegnale): Promise<void> {
    if (this.chiusa) return;
    try {
      if ("descrizione" in d) {
        const desc = d.descrizione;
        const collisione = desc.type === "offer" && (this.facendoOfferta || this.pc.signalingState !== "stable");
        this.ignoraOfferta = !this.o.educato && collisione;
        if (this.ignoraOfferta) return;
        await this.pc.setRemoteDescription(desc);
        if (desc.type === "offer") {
          await this.pc.setLocalDescription();
          const loc = this.pc.localDescription;
          if (loc && (loc.type === "offer" || loc.type === "answer")) this.o.inviaSegnale({ descrizione: { type: loc.type, sdp: loc.sdp } });
        }
      } else if ("candidato" in d) {
        try {
          await this.pc.addIceCandidate(d.candidato ?? undefined);
        } catch (e) {
          if (!this.ignoraOfferta) throw e;
        }
      }
    } catch {
      // un messaggio fuori ordine: la negoziazione successiva rimette le cose a posto
    }
  }

  /**
   * Cambia la traccia video inviata (es. dal video grezzo a quello con il filtro) senza rinegoziare:
   * l'avversario non vede interruzioni.
   */
  async sostituisciVideo(traccia: MediaStreamTrack): Promise<void> {
    if (this.chiusa) return;
    const mittente = this.pc.getSenders().find((s) => s.track?.kind === "video" || (s.track === null && this.pc.getTransceivers().some((t) => t.sender === s && t.receiver.track.kind === "video")));
    if (mittente && mittente.track !== traccia) await mittente.replaceTrack(traccia);
  }

  /** v026: cambia la traccia audio inviata (voce buffa o microfono vero), senza rinegoziare. */
  async sostituisciAudio(traccia: MediaStreamTrack): Promise<void> {
    if (this.chiusa) return;
    const mittente = this.pc.getSenders().find((s) => s.track?.kind === "audio" || (s.track === null && this.pc.getTransceivers().some((t) => t.sender === s && t.receiver.track.kind === "audio")));
    if (mittente && mittente.track !== traccia) await mittente.replaceTrack(traccia);
  }

  /** Statistiche per capire se il video passa dal TURN (pannello di prova e diagnostica). */
  async passaDalTurn(): Promise<boolean> {
    const stats = await this.pc.getStats();
    let coppia: RTCStats | undefined;
    stats.forEach((s) => {
      if (s.type === "transport" && (s as unknown as { selectedCandidatePairId?: string }).selectedCandidatePairId) {
        coppia = stats.get((s as unknown as { selectedCandidatePairId: string }).selectedCandidatePairId);
      }
    });
    const locale = coppia ? stats.get((coppia as unknown as { localCandidateId: string }).localCandidateId) : undefined;
    return (locale as unknown as { candidateType?: string } | undefined)?.candidateType === "relay";
  }

  chiudi(): void {
    this.chiusa = true;
    if (this.timerRiavvio) clearTimeout(this.timerRiavvio);
    this.pc.ontrack = null;
    this.pc.onicecandidate = null;
    this.pc.onnegotiationneeded = null;
    this.pc.onconnectionstatechange = null;
    this.pc.close();
  }
}

export type ErroreFotocamera = "negato" | "assente" | "occupata" | "non_supportata";

/** Chiede fotocamera frontale e microfono. */
export async function apriFotocamera(o: { audio?: boolean } = {}): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) throw "non_supportata" satisfies ErroreFotocamera;
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24, max: 30 } },
      audio: o.audio === false ? false : { echoCancellation: true, noiseSuppression: true },
    });
  } catch (e) {
    const nome = (e as DOMException).name;
    if (nome === "NotAllowedError" || nome === "SecurityError") throw "negato" satisfies ErroreFotocamera;
    if (nome === "NotFoundError" || nome === "OverconstrainedError") throw "assente" satisfies ErroreFotocamera;
    if (nome === "NotReadableError" || nome === "AbortError") throw "occupata" satisfies ErroreFotocamera;
    throw "non_supportata" satisfies ErroreFotocamera;
  }
}
